import { createPlaylistItem, interactionTargetContentIds, requireDeviceProfileId, Orientation } from "./authoring.js";
import { validatePlaylistAudio, validatePreparedAudio, isAudioAsset } from "./playlist-audio.js";
import { validateOverlayPlaylist } from "./hotspot-overlay.js";

const list = value => Array.isArray(value) ? value : [];
const bytesOf = value => value instanceof Uint8Array ? value.slice() : new Uint8Array(value).slice();

// Use the same target preparation and coordinate projection as Cloud publishing,
// with a local artifact sink. Source files and saved playlists stay untouched.
export async function prepareUsbPlaylist(snapshot, playlistId, device, { prepareVariant, isCurrent = () => true } = {}) {
  const check = () => { if (!isCurrent()) throw new Error("The account or selected Pirdy changed. Nothing was activated."); };
  check();
  if (typeof prepareVariant !== "function") throw new TypeError("Target artwork preparation is required.");
  const playlist = list(snapshot?.playlists).find(value => value.id === playlistId);
  if (!playlist || !playlist.items?.length || playlist.items.length > 16) throw new Error("Choose a saved playlist with 1 to 16 items.");
  const profileId = requireDeviceProfileId(device?.hardwareProfile);
  const orientation = String(device.orientation).toLowerCase() === "landscape" ? Orientation.landscape
    : String(device.orientation).toLowerCase() === "portrait" ? Orientation.portrait : null;
  if (!orientation) throw new Error("The Pirdy screen orientation is unknown.");
  const content = list(snapshot.assets), byId = new Map(content.map(asset => [asset.assetId, asset]));
  const audioIds = validatePlaylistAudio(playlist.items, content);
  validateOverlayPlaylist(playlist.items);
  const planned = playlist.items.map(item => ({ item: structuredClone(item), include: true }));
  const plannedIds = new Set(planned.map(entry => entry.item.assetId));
  for (const id of interactionTargetContentIds(playlist.items)) if (!plannedIds.has(id)) {
    const asset = byId.get(id);
    if (!asset || isAudioAsset(asset)) throw new Error("A hotspot destination is missing from this account.");
    planned.push({ item: { ...createPlaylistItem(asset), autoplay: false }, include: false });plannedIds.add(id);
  }
  if (plannedIds.size > 32) throw new Error("This playlist has too many additional hotspot destinations.");
  const uploads = new Map(), assets = [], items = [], targetIds = new Map(), sources = new Map();
  const storePrepared = async prepared => {
    check();
    const hash = String(prepared.metadata.sha256).toLowerCase();
    if (!/^[a-f0-9]{64}$/.test(hash)) throw new Error("Prepared artwork could not be verified.");
    const path = `/assets/${hash}.pimg`;
    if (!uploads.has(path)) uploads.set(path, { path, bytes: new Uint8Array(await prepared.blob.arrayBuffer()) });
    return { assetId: crypto.randomUUID(), alreadyExists: true };
  };
  try {
    for (const entry of planned) {
      check();
      const original = byId.get(entry.item.assetId);
      if (!original || isAudioAsset(original)) throw new Error("Playlist artwork is missing from this account.");
      if (!sources.has(original.assetId)) sources.set(original.assetId,
        new File([original.bytes], original.name + (original.mediaType === "image/jpeg" ? ".jpg" : ".png"), { type: original.mediaType }));
      const result = await prepareVariant(entry.item, { ...original, logicalSourceAssetId: original.assetId },
        { profileId, orientation }, new Map([[original.assetId, "browser-local"]]), sources, check, storePrepared);
      check();
      const mode = Number(result.asset.playbackMode), hash = String(result.asset.sha256).toLowerCase();
      const path = `/assets/${hash}.pimg`, storageLayout = mode === 2 ? "column-major" : "row-major";
      if (!uploads.has(path)) throw new Error("Artwork preparation did not produce its verified file.");
      const renderer = { 1: "static", 2: "st7789-hardware-scroll", 3: "software-vertical-scroll" }[mode];
      if (!renderer) throw new Error("This artwork cannot be published by USB.");
      assets.push({ ...result.asset, path, storageLayout, renderer });
      targetIds.set(original.assetId, result.asset.assetId);
      if (entry.include) items.push({ ...result.item, path, storageLayout, renderer,
        ...(mode === 1 ? {} : { axis: mode === 2 ? "horizontal" : "vertical" }) });
    }
    for (const id of audioIds) {
      const original = byId.get(id), bytes = bytesOf(original.bytes);
      validatePreparedAudio(bytes);check();
      const hash = [...new Uint8Array(await crypto.subtle.digest("SHA-256", bytes))].map(value => value.toString(16).padStart(2, "0")).join("");
      if (hash !== String(original.sha256).toLowerCase()) throw new Error("Saved audio failed verification. Re-import it before publishing.");
      const path = `/assets/${hash}.wav`;
      assets.push({ assetId: id, name: original.name, sha256: hash, fileSize: bytes.length,
        width: 0, height: 0, orientation, playbackMode: 5, pixelFormat: 2, path });
      uploads.set(path, { path, bytes });
    }
    for (const item of items) for (const area of list(item.interactions?.touchAreas)) {
      const id = area.action?.targetContentId;
      if (id) { if (!targetIds.has(id)) throw new Error("A hotspot destination could not be prepared.");area.action.targetContentId = targetIds.get(id); }
    }
    check();
    const manifest = { version: Date.now(), name: playlist.name, targetProfileId: profileId,
      orientation, items, assets };
    const bytes = new TextEncoder().encode(JSON.stringify(manifest));
    if (bytes.length > 24576) throw new Error("This playlist exceeds the device metadata limit. Reduce its hotspot or overlay data.");
    return [...uploads.values(), { path: "/manifests/active.json", bytes }];
  } catch (error) { for (const file of uploads.values()) file.bytes.fill(0);throw error; }
}

export async function sendUsbPlaylist(client, files, { isCurrent = () => true, progress = () => {} } = {}) {
  const manifest = files.at(-1);
  if (manifest?.path !== "/manifests/active.json" || files.slice(0, -1).some(file => !file.path.startsWith("/assets/")))
    throw new Error("The playlist transfer is incomplete.");
  client.validateManifest(manifest.bytes);
  if (!isCurrent()) throw new Error("The account or selected Pirdy changed.");
  await client.purpose("storage");
  try {
    const storage = await client.request(3), size = files.reduce((total, file) => total + file.bytes.length, 0);
    if (!storage.available || storage.freeBytes < size + 65536) throw new Error("The microSD card is unavailable or does not have enough free space.");
    for (let index = 0; index < files.length; index++) {
      if (!isCurrent()) throw new Error("Transfer cancelled before activation.");
      await client.upload(files[index].path, files[index].bytes, isCurrent,
        (done, total) => progress(index + 1, files.length, done, total));
    }
  } finally {
    for (const file of files) file.bytes.fill(0);
    if (!client.faulted && isCurrent()) { try { await client.request(17);await client.purpose("control"); } catch { await client.disconnect(); } }
  }
}
